import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2, Image as ImageIcon } from 'lucide-react';

const ManageGallery = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    // Form State
    const [title, setTitle] = useState('');
    const [type, setType] = useState('products');
    const [image, setImage] = useState(null);

    useEffect(() => {
        fetchGallery();
    }, []);

    const fetchGallery = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewgalleries`, {
                headers: { authorization: `Bearer ${token}` }
            });
            
            const data = res.data.gallery || res.data.galleries || [];
            setItems(Array.isArray(data) ? data : []);
        } catch (err) { 
            console.error("Fetch error:", err);
            setItems([]); // Fallback to empty array to prevent .map() errors
        } finally {
            setFetchLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!image) return alert("Please select an image");
        
        setLoading(true);
        const formData = new FormData();
        formData.append('title', title);
        formData.append('type', type);
        formData.append('image', image);

        try {
            const token = localStorage.getItem('adminToken');
            await axios.post(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/insertgallery`, formData, {
                headers: { 
                    'Content-Type': 'multipart/form-data', 
                    authorization: `Bearer ${token}` 
                }
            });
            alert("Media added to Gallery!");
            setTitle(''); 
            setImage(null);
            // Reset the file input manually if needed
            e.target.reset(); 
            fetchGallery();
        } catch (err) {
            alert(err.response?.data?.message || "Failed to upload");
        } finally {
            setLoading(false);
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm("Delete this image from gallery?")) return;
        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/deletegallery/${id}`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setItems(items.filter(item => item._id !== id));
        } catch (err) {
            alert("Delete failed");
        }
    };

    // Filter logic: Safe handling of the items array
    const filteredItems = (items || []).filter(item => 
        filter === 'all' ? true : item.type === filter
    );

    if (fetchLoading) {
        return (
            <div className="h-screen flex flex-col items-center justify-center gap-4 text-slate-400 font-bold uppercase tracking-widest text-[10px]">
                <Loader2 className="animate-spin text-orange-500" size={40} />
                Initializing Assets...
            </div>
        );
    }

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-10">
            <h1 className="text-3xl font-black uppercase italic">Visual <span className="text-orange-500">Assets</span></h1>

            {/* INSERT FORM */}
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Image Title / Caption</label>
                        <input 
                            type="text" 
                            value={title} 
                            onChange={(e)=>setTitle(e.target.value)} 
                            className="w-full p-3 bg-slate-50 rounded-xl font-bold border border-transparent focus:border-orange-500 outline-none transition-all" 
                            placeholder="Ex: Factory Floor"
                            required 
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase text-slate-400">Gallery Category</label>
                        <select 
                            value={type} 
                            onChange={(e)=>setType(e.target.value)} 
                            className="w-full p-3 bg-slate-50 rounded-xl font-bold border border-transparent focus:border-orange-500 outline-none" 
                            required
                        >
                            <option value="products">Products</option>
                            <option value="company">Company</option>
                            <option value="newspaper">Newspaper</option>
                        </select>
                    </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                    <label className="text-[10px] font-black uppercase text-slate-400">Upload File</label>
                    <input 
                        type="file" 
                        onChange={(e)=>setImage(e.target.files[0])} 
                        className="w-full mt-2 text-xs file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100" 
                        required 
                    />
                </div>

                <button 
                    type="submit" 
                    disabled={loading} 
                    className="w-full py-4 bg-orange-500 text-white font-black uppercase rounded-2xl hover:bg-slate-900 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
                >
                    {loading ? <Loader2 className="animate-spin" /> : <><Plus size={18}/> Add to Gallery</>}
                </button>
            </form>

            {/* FILTER TABS */}
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                {['all', 'company', 'products', 'newspaper'].map((t) => (
                    <button 
                        key={t}
                        onClick={() => setFilter(t)}
                        className={`px-6 py-2 rounded-full text-[10px] font-black uppercase transition-all border ${filter === t ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-400 border-slate-100 hover:border-orange-500'}`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {/* GALLERY TABLE */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-900 text-white text-[10px] uppercase">
                            <tr>
                                <th className="p-5">Preview</th>
                                <th className="p-5">Title</th>
                                <th className="p-5">Type</th>
                                <th className="p-5 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredItems.length > 0 ? (
                                filteredItems.map((item) => (
                                    <tr key={item._id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="p-5">
                                            <div className="w-20 h-12 bg-slate-100 rounded-lg overflow-hidden border">
                                                <img src={item.image} className="w-full h-full object-cover" alt="gallery" />
                                            </div>
                                        </td>
                                        <td className="p-5 font-bold uppercase text-slate-700 text-sm">{item.title}</td>
                                        <td className="p-5">
                                            <span className="text-[9px] font-black px-3 py-1 bg-orange-100 text-orange-600 rounded-full uppercase">
                                                {item.type}
                                            </span>
                                        </td>
                                        <td className="p-5 text-center">
                                            <button 
                                                onClick={() => deleteItem(item._id)}
                                                className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-sm"
                                            >
                                                <Trash2 size={16}/>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="p-10 text-center text-slate-400 text-xs italic">
                                        No images found in this category.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ManageGallery;